/**
 * API Structures for Vendor App
 * Contains Request (DTO) and Response shapes for Order, Menu, and Vendor management.
 */

// --- ENUMS ---

export enum OrderStatus {
  PAYMENTINITIATED = 'PAYMENTINITIATED',
  CREATED = 'CREATED',
  ORDERACCEPTED = 'ORDERACCEPTED',
  PREPARING = 'PREPARING',
  READY = 'READY',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  REJECTED = 'REJECTED',
  PARTIALORDERCHECK = 'PARTIALORDERCHECK',
  ONLAZYTRACK = 'ONLAZYTRACK',
  PREPARINGREQUEST = 'PREPARINGREQUEST',
  SERVING = 'SERVING',
  PAYMENTPENDING = 'PAYMENTPENDING',
  PAYMENTCOMPLETED = 'PAYMENTCOMPLETED',
  PAYMENTFAILED = 'PAYMENTFAILED'
}

export enum OrderType {
  NEARBY = 'NEARBY',
  ONTHEGO = 'ONTHEGO'
}

export enum ServiceType {
  INDOOR = '1',
  OUTDOOR = '2',
  INDOUT = '3',
  INDOUTTKAWY = '4',
  TKAWY = '5',
  INDTKAWY = '6',
  OUTTKAWY = '7'
}

export enum FoodItemStatus {
  Available = 'Available',
  NotAvailable = 'Not Available'
}

export enum VegOrNonveg {
  VEG = 'VEG',
  NONVEG = 'NONVEG',
  EGG = 'EGG'
}

export enum RegisteredStatus {
  REGISTERED = 'Registered',
  NOT_REGISTERED = 'NotRegistered',
  PENDING = 'Pending'
}

export enum RestaurantLive {
  LIVE = 'LIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED'
}

// --- COMMON TYPES ---

export interface Address {
  address: string
  latlng: string
  city: string
  pincode: number
}

export interface WorkingHour {
  restaurantOpen: 'Open' | 'Closed'
  workingHours: {
    open: string | null
    close: string | null
  }
  rushHours?: Array<{ startTime: string; endTime: string }>
}

export interface WorkingDaysAndHours {
  Monday: WorkingHour
  Tuesday: WorkingHour
  Wednesday: WorkingHour
  Thursday: WorkingHour
  Friday: WorkingHour
  Saturday: WorkingHour
  Sunday: WorkingHour
}

// --- ORDER MANAGEMENT ---

// GET /api/vendor/getActiveOrdersByRestaurant/:restaurantId
export interface GetActiveOrdersResponse {
  responseCode: number
  orders: Order[]
}

// GET /api/vendor/getOrderDetails/:orderId
export interface GetOrderDetailsResponse {
  responseCode: number
  orderDetails: Order
}

// POST /api/vendor/updateOrderStatus
export interface UpdateOrderStatusRequest {
  orderId: string
  orderStatus: OrderStatus
}

export interface UpdateOrderStatusResponse {
  responseCode: number
  orderId: string
}

export interface Order {
  orderId: string
  userId: string
  orderStatus: OrderStatus
  orderType: OrderType
  serviceType: string
  orderDateTime: string // ISO Date String
  orderReachTime: number // BigInt represented as number/string
  totalAmount: number
  gst: number
  orderTotalWithoutDiscount?: number
  discountAmount?: number
  headCount?: number | null
  requestedAmmenities?: Array<{ ammenityName: string }> | null
  packagingCharges?: number
  packagingGstTotal?: number
  orderItems: OrderItem[]
}

export interface OrderItem {
  foodId: string
  categoryId: number
  subCategoryId: number
  quantity: number
  price: number
  foodStatus: string
  gstAmount: number
  packagingCharges: number
  originalBasePrice?: number
  discountAmount?: number
  label?: string // Enriched via transform
  subCategory?: string // Enriched via transform
  type?: VegOrNonveg // Enriched via transform
}

// --- MENU MANAGEMENT ---

// GET /api/vendor/menu/live/:restaurantId
export interface GetLiveMenuResponse {
  responseCode: number
  data: {
    versionId: string | null
    approvedAt: string | null
    menu: SetMenuDetails | null
  } | null
}

// GET /api/vendor/menu/draft/:restaurantId
export interface GetDraftMenuResponse {
  responseCode: number
  data: {
    versionId: string
    restaurantId: string
    status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED'
    menuCard: MenuCategory[]
    addonGroups: AddonGroup[]
    timeSlots: TimeSlotsRange[]
    createdAt: string
    updatedAt: string
  } | null
}

export interface SetMenuDetails {
  restaurantId: string
  addonGroups: AddonGroup[]
  menuCard: MenuCategory[]
  timeSlots: TimeSlotsRange[]
  restaurantOffers?: any[]
}

export interface MenuCategory {
  key: string
  category_id: string
  label: string
  hasSubCategory: boolean
  children: Array<SubCategory | FoodItem>
}

export interface SubCategory {
  key: string
  category_id: string
  label: string
  subcategory_id: string
  foodItems: FoodItem[]
}

export interface FoodItem {
  food_id: string
  label: string
  price: number
  totalPrice: number
  description: string
  gstPercentage: number
  gst: number
  packaging: boolean
  packaging_charges: number | null
  servings: number
  categoryType: 'Fresh' | 'Packaged'
  status: FoodItemStatus
  type: VegOrNonveg
  healthy: boolean
  vegan: boolean
  recommend: boolean
  cuisine: string
  images: Array<{ uid: string; name: string; url: string }>
  availableDays: string[]
  availableHours: Record<string, string[]>
  stock: {
    initialStockPerDay: number
    currentStock: number
    autoRefresh: boolean
  }
}

export interface AddonGroup {
  group_id: string
  label: string
  selectionType: 'SINGLE_SELECT' | 'MULTI_SELECT'
  minSelect: number
  maxSelect: number
  addons: Array<{
    addon_id: string
    label: string
    price: number
    isDefault: boolean
  }>
}

export interface TimeSlotsRange {
  slotName: string
  availableHours: { from: string; to: string }
  type: 'DEFAULT' | 'CUSTOM'
  crossingOver: boolean
}

// --- VENDOR / RESTAURANT DETAILS ---

// POST /api/vendor/getvendordetails
export interface GetVendorDetailsRequest {
  mobileNumber: number
  type: 'vendorRegistrationDetails' | 'vendorRestaurantDetails'
}

export interface GetVendorDetailsResponse {
  responseCode: number
  vendorInfo: VendorProfile
}

// POST /api/vendor/saverestaurantdetails
export type SaveRestaurantDetailsRequest = VendorProfile

export interface SaveRestaurantDetailsResponse {
  responseCode: number
  message: string
  updatedVendorInfo: any
}

export interface VendorProfile {
  vendorId?: string // Optional depending on context
  ownersFullName: string
  ownerEmailAddress: string
  mobileNumber: number
  restaurantId: string | null
  restaurantName: string
  restaurantAddress: Address
  workingDaysAndHours: WorkingDaysAndHours
  gstCategoryType: string | null
  serviceType: string | null
  pan: string
  gstin: string
  ifscCode: string
  bankAccNumber: string
  fssaiCode: string
  priceForTwo: number
  cuisine: Array<{ label: string; value: string }>
  amenities: Array<{
    label: string
    value: string
    icon: string
    selected: boolean
  }>
  pureVeg?: boolean
  maxHeadCountPerOrder?: number
  live: RestaurantLive
  registeredStatus?: RegisteredStatus
  imageCategory?: Record<string, any>
}
